import {
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useNoti from "../../../hooks/useNoti";
import { AppNotifications } from "../../../models";
import { ChatService } from "../../../services";
import Loading from "../../components/Loading";
import { MESSAGES } from "../../constants/mock";
import "./index.scss";

type ChatMessage = {
  _id?: string;
  role: "user" | "assistant";
  content: string;
};

type ChatSession = {
  _id: string;
  title: string;
  updatedAt: string;
};

const SUGGESTED_QUESTIONS = [
  "Who was late today?",
  "Who is absent today?",
  "Who forgot to check out today?",
  "Give me today's attendance summary",
];

const HrAssistant: React.FC = () => {
  const [pushNoti] = useNoti();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await ChatService.getSessions();
      if (res?.status === 200) {
        setSessions(res.data?.data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setBootLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const res = await ChatService.getSession(id);
      if (res?.status === 200) {
        setSessionId(id);
        setMessages(res.data?.data?.messages || []);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setLoading(false);
    }
  }, [pushNoti]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text: string) => {
    const message = text.trim();
    if (!message || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setLoading(true);

    try {
      const res = await ChatService.sendMessage(message, sessionId);
      if (res?.status === 200 && res.data?.data) {
        const { sessionId: newSessionId, reply } = res.data.data;
        setSessionId(newSessionId);
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        fetchSessions();
      } else {
        pushNoti(AppNotifications.ERROR, res?.data?.message || MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = () => {
    setSessionId(undefined);
    setMessages([]);
    setInput("");
  };

  const deleteSession = async (id: string) => {
    try {
      const res = await ChatService.deleteSession(id);
      if (res?.status === 200) {
        if (sessionId === id) startNewChat();
        fetchSessions();
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    }
  };

  if (bootLoading) {
    return <Loading />;
  }

  return (
    <div className='hr-assistant'>
      <div className='hr-assistant__sidebar'>
        <div className='hr-assistant__sidebar__header'>
          <Typography variant='h6'>Sessions</Typography>
          <Button size='small' onClick={startNewChat}>
            New chat
          </Button>
        </div>
        <List dense className='hr-assistant__sessions'>
          {sessions.map((session) => (
            <ListItem
              key={session._id}
              disablePadding
              secondaryAction={
                <IconButton
                  edge='end'
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSession(session._id);
                  }}
                >
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              }
            >
              <ListItemButton
                selected={sessionId === session._id}
                onClick={() => loadSession(session._id)}
              >
                <ListItemText
                  primary={session.title || "HR Assistant"}
                  secondary={new Date(session.updatedAt).toLocaleString()}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </div>

      <div className='hr-assistant__main'>
        <div className='hr-assistant__header'>
          <ChatBubbleOutlineIcon color='primary' />
          <div>
            <Typography variant='h5'>HR Assistant</Typography>
            <Typography variant='body2' color='text.secondary'>
              Ask about attendance, late arrivals, absences, and more.
            </Typography>
          </div>
        </div>

        <div className='hr-assistant__messages'>
          {!messages.length && (
            <div className='hr-assistant__welcome'>
              <Typography variant='h6'>How can I help you today?</Typography>
              <div className='hr-assistant__suggestions'>
                {SUGGESTED_QUESTIONS.map((question) => (
                  <Chip
                    key={question}
                    label={question}
                    clickable
                    onClick={() => sendMessage(question)}
                  />
                ))}
              </div>
            </div>
          )}

          {messages.map((message, index) => (
            <Paper
              key={`${message.role}-${index}`}
              className={`hr-assistant__bubble hr-assistant__bubble--${message.role}`}
              elevation={0}
            >
              <Typography
                component='div'
                sx={{ whiteSpace: "pre-wrap", fontSize: "1.4rem" }}
              >
                {message.content}
              </Typography>
            </Paper>
          ))}

          {loading && (
            <Paper className='hr-assistant__bubble hr-assistant__bubble--assistant' elevation={0}>
              <Typography sx={{ fontSize: "1.4rem", color: "#6c778d" }}>
                Thinking...
              </Typography>
            </Paper>
          )}
          <div ref={bottomRef} />
        </div>

        <Box className='hr-assistant__input'>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder='Ask a question about attendance...'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            disabled={loading}
          />
          <Button
            variant='contained'
            endIcon={<SendIcon />}
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
          >
            Send
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default HrAssistant;
